import { z } from 'zod';
import dayjs, { Dayjs } from 'dayjs';
import { PaylinkCustomerInfoResponse } from '../../../store/modules/paylink/types';

export enum DeliveryMethod {
  TEXT = 'TEXT',
  EMAIL = 'EMAIL',
  TEXT_AND_EMAIL = 'TEXT_AND_EMAIL',
}

export enum CustomerFieldType {
  PHONE = 'customerPhone',
  EMAIL = 'customerEmail',
}

export const sanitizeNumericString = (phone?: string | null) => phone?.replaceAll(/\D/g, '') ?? '';

export const PAYLINK_DESCRIPTION_MAX_LENGTH = 255;

export const willPasteExceedDescriptionLimit = (
  currentValue: string,
  pastedText: string,
  selectionStart: number,
  selectionEnd: number,
  maxLength = PAYLINK_DESCRIPTION_MAX_LENGTH,
): boolean => {
  const selectedLength = Math.max(0, selectionEnd - selectionStart);
  const available = maxLength - (currentValue.length - selectedLength);
  return pastedText.length > available;
};

export const getPaylinkDescriptionHelperText = (
  description: string | undefined,
  errorMessage?: string,
  pasteTrimmedMessage?: string,
): string | undefined => {
  if (errorMessage) return errorMessage;
  if (pasteTrimmedMessage) return pasteTrimmedMessage;

  const length = description?.length ?? 0;
  if (length > 0) {
    return `${length}/${PAYLINK_DESCRIPTION_MAX_LENGTH}`;
  }
  return undefined;
};

/**
 * Returns the type of customer field based on the input value. These are simple regex checks
 * and are not meant to be a comprehensive validation of the input value.
 * @param customer The customer input value
 * @returns The type of customer field (phone or email)
 */
export const getFieldTypeFromInput = (customer: string): CustomerFieldType | null => {
  const emailSchema = z.string().email();
  const phoneRegex = /^\d{10}$/;
  const noLettersRegex = /^[^a-zA-Z]+$/;

  if (emailSchema.safeParse(customer).success) {
    return CustomerFieldType.EMAIL;
  }

  if (noLettersRegex.test(customer) && phoneRegex.test(sanitizeNumericString(customer))) {
    return CustomerFieldType.PHONE;
  }

  return null;
};

/**
 * Returns the customer fields that are missing based on the delivery method and the customer input value.
 * @param customerInputValue The customer input value
 * @param deliveryMethod The selected delivery method
 * @param selectedCustomer The selected customer (if any) retrieved from the customer search
 * @returns The missing customer fields as a set
 */
export const getMissingCustomerFields = (
  customerInputValue: string,
  deliveryMethod: DeliveryMethod | null,
  selectedCustomer?: PaylinkCustomerInfoResponse | null,
): Set<CustomerFieldType> => {
  const missingRequiredFields: Set<CustomerFieldType> = new Set();
  if (!deliveryMethod) return missingRequiredFields;

  if (deliveryMethod === DeliveryMethod.TEXT || deliveryMethod === DeliveryMethod.TEXT_AND_EMAIL) {
    missingRequiredFields.add(CustomerFieldType.PHONE);
  }

  if (deliveryMethod === DeliveryMethod.EMAIL || deliveryMethod === DeliveryMethod.TEXT_AND_EMAIL) {
    missingRequiredFields.add(CustomerFieldType.EMAIL);
  }

  if (selectedCustomer) {
    if (selectedCustomer.email) missingRequiredFields.delete(CustomerFieldType.EMAIL);
    if (selectedCustomer.phone) missingRequiredFields.delete(CustomerFieldType.PHONE);
    return missingRequiredFields;
  }

  const typeFromCustomerInput = getFieldTypeFromInput(customerInputValue);
  if (typeFromCustomerInput) {
    // If the customer input value is a valid email or phone number, then it is not a missing field
    missingRequiredFields.delete(typeFromCustomerInput);
  }

  return missingRequiredFields;
};

export const ExpirationSchema = z
  .instanceof(dayjs as unknown as typeof Dayjs)
  .refine((date) => date && date.isAfter(dayjs()), {
    message: 'Expiration date cannot be in the past.',
  })
  .optional()
  .nullish();

export const getValidationSchema = (selectedCustomer: PaylinkCustomerInfoResponse | null) =>
  z
    .object({
      customer: z
        .string()
        .nonempty('Customer is required')
        .refine(
          (customer) => {
            const getInputType = getFieldTypeFromInput(customer);
            // If there is a selected customer, then the customer input is not required, it is only
            // used to display the customer's name/email/phone number
            return getInputType !== null || selectedCustomer;
          },
          {
            message: 'Invalid phone number or email',
          },
        ),
      description: z
        .string()
        .max(PAYLINK_DESCRIPTION_MAX_LENGTH, 'Description must be less than 255 characters')
        .optional(),
      merchantInvoiceId: z.string().optional(),
      amount: z
        .string()
        .nonempty('Amount is required')
        .refine(
          (amount) => {
            const amountNumber = Number(sanitizeNumericString(amount as string));
            return !Number.isNaN(amountNumber) && amountNumber > 0;
          },
          {
            message: 'Invalid amount',
          },
        ),
      expiration: ExpirationSchema,
      deliveryMethod: z
        .nativeEnum(DeliveryMethod)
        .nullable()
        .superRefine((deliveryMethod, ctx) => {
          if (deliveryMethod === null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Delivery method is required',
            });
          }
        }),
      saveCustomerInformation: z.boolean().optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      [CustomerFieldType.EMAIL]: z.string().optional(),
      [CustomerFieldType.PHONE]: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      // The true validation for the email/phone is done here. This is because the email/phone
      // are optional and the validation is done only if the email/phone are missing. This is
      // not possible with the zod validation schema.
      const missingCustomerFields = getMissingCustomerFields(
        data.customer,
        data.deliveryMethod,
        selectedCustomer,
      );
      if (missingCustomerFields.size === 0) return;

      if (missingCustomerFields.has(CustomerFieldType.EMAIL)) {
        if (!data[CustomerFieldType.EMAIL] || data[CustomerFieldType.EMAIL] === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Email is required',
            path: [CustomerFieldType.EMAIL],
          });
        }
        if (!z.string().email().safeParse(data[CustomerFieldType.EMAIL]).success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid email',
            path: [CustomerFieldType.EMAIL],
          });
        }
      }

      if (missingCustomerFields.has(CustomerFieldType.PHONE)) {
        if (!data[CustomerFieldType.PHONE] || data[CustomerFieldType.PHONE] === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Phone number is required',
            path: [CustomerFieldType.PHONE],
          });
        }
        if (
          !z
            .string()
            .regex(/^\d{10}$/)
            .safeParse(sanitizeNumericString(data[CustomerFieldType.PHONE] as string)).success
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid phone number',
            path: [CustomerFieldType.PHONE],
          });
        }
      }
    });

export type FormValues = z.infer<ReturnType<typeof getValidationSchema>>;
